import { Capacitor } from '@capacitor/core';
import { preferences } from '@apexx/storage';

/**
 * Bump when a release must force fresh FCM tokens on all devices (e.g. stale-token recovery).
 * Stored in Preferences; migration runs once per bump per device.
 */
export const FCM_TOKEN_SYNC_VERSION = '2';

const SYNC_VERSION_KEY = 'fcm_token_sync_version';
const LAST_TOKEN_KEY = 'fcm_last_registered_token';

export async function getPersistedFcmToken(): Promise<string | null> {
	if (!Capacitor.isNativePlatform()) {
		return null;
	}

	return preferences.getItem(LAST_TOKEN_KEY);
}

export async function persistRegisteredFcmToken(token: string): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	await preferences.setItem(LAST_TOKEN_KEY, token);
}

export async function clearPersistedFcmToken(): Promise<void> {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	await preferences.removeItem(LAST_TOKEN_KEY);
}

/**
 * When sync version changes, unregister the cached token and reset the Firebase instance
 * so the next {@link FCM.getToken} returns a fresh registration token.
 */
export async function ensureFcmTokenSyncVersion(
	onUnregisterOldToken?: (token: string) => Promise<void>,
): Promise<{ migrated: boolean }> {
	if (!Capacitor.isNativePlatform()) {
		return { migrated: false };
	}

	const storedVersion = await preferences.getItem(SYNC_VERSION_KEY);

	if (storedVersion === FCM_TOKEN_SYNC_VERSION) {
		return { migrated: false };
	}

	const oldToken = await preferences.getItem(LAST_TOKEN_KEY);

	if (oldToken && onUnregisterOldToken) {
		try {
			await onUnregisterOldToken(oldToken);
		} catch (err) {
			console.warn('[FCM] Failed to unregister stale token during migration:', err);
		}
	}

	try {
		const { FCM } = await import('@capacitor-community/fcm');
		await FCM.deleteInstance();
	} catch (err) {
		console.warn('[FCM] deleteInstance during migration failed:', err);
	}

	await preferences.setItem(SYNC_VERSION_KEY, FCM_TOKEN_SYNC_VERSION);
	await preferences.removeItem(LAST_TOKEN_KEY);

	console.info(`[FCM] Token sync migration complete (v${FCM_TOKEN_SYNC_VERSION})`);

	return { migrated: true };
}
